import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '../../api/client.js';
import { settingsHistoryKey, type SettingsHistorySubject } from '../../api/settings-history.api';
import { restoreRequestsKey, type FourEyesRestoreApi, type RestoreChange, type RestoreRequest } from '../../api/settings-restore.api';
import { useAuth } from '../../auth/useAuth.js';
import { APP_LOCALE } from '../../utils/locale.js';

import { describeValue } from './settings-changes';
import { SettingsSaveBar } from './SettingsSaveBar';

/**
 * "Restore this version" (DEV-142), shared by every section that offers it.
 *
 * - RestoreSaveBar: the restore goes through the same save bar and review as
 *   an edit, every change needing a reason; a four-eyes section proposes it
 *   instead of saving it.
 * - PendingRestorePanel: the restore waiting for a second approver, with
 *   Approve (not for its proposer) and Reject.
 * - RestoreReasonDialog: for the pages whose versions are a list (fees,
 *   onboarding policy), the same reason prompt in a dialog.
 */

/** The shortest reason the API accepts for a restore. */
export const RESTORE_MIN_REASON = 10;

export interface RestorePlan {
  /** e.g. "version 2". */
  readonly label: string;
  readonly changes: readonly RestoreChange[];
  readonly warning?: string;
  /** A box to tick before it can be saved (LIVE). */
  readonly confirmLabel?: string;
}

interface RestoreSaveBarProps {
  readonly plan: RestorePlan;
  readonly fourEyes?: boolean;
  readonly saving: boolean;
  readonly error?: string;
  readonly onConfirm: (reason: string) => void;
  readonly onCancel: () => void;
}

export function RestoreSaveBar({ plan, fourEyes = false, saving, error, onConfirm, onCancel }: RestoreSaveBarProps): JSX.Element | null {
  const changes = plan.changes.map((c) => ({ ...c, secret: false, needsReason: true }));
  return (
    <SettingsSaveBar
      sticky
      changes={changes}
      saving={saving}
      onSave={(reason) => onConfirm(reason ?? '')}
      onDiscard={onCancel}
      discardLabel="Cancel restore"
      summary={`Restoring ${plan.label}: ${String(changes.length)} change${changes.length === 1 ? '' : 's'}`}
      saveLabel={fourEyes ? 'Propose restore' : 'Restore'}
      reviewTitle={`Restore ${plan.label}`}
      reasonMinLength={RESTORE_MIN_REASON}
      reasonPrompt={fourEyes
        ? 'Nothing changes yet: a second person who may change this section has to approve the restore. The reason is kept in the History.'
        : 'The earlier values are saved again as a new version. The reason is kept in the History; nothing in it is edited.'}
      {...(plan.warning === undefined ? {} : { warning: plan.warning })}
      {...(plan.confirmLabel === undefined ? {} : { confirmLabel: plan.confirmLabel })}
      {...(error === undefined || error === '' ? {} : { error })}
    />
  );
}

const message = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

interface PendingRestorePanelProps {
  readonly api: FourEyesRestoreApi;
  readonly base: string;
  readonly subject: SettingsHistorySubject;
  /** The person may change the section, so may approve (if not the proposer) or reject. */
  readonly canDecide: boolean;
  /** After an approval: the section's own data changed. */
  readonly onApproved?: () => void;
  readonly children?: ReactNode;
}

function ChangeList({ changes }: { changes: readonly RestoreChange[] }): JSX.Element {
  return (
    <Box component="ul" sx={{ pl: 2, my: 1 }}>
      {changes.map((c) => (
        <Typography component="li" variant="body2" key={c.key} sx={{ wordBreak: 'break-word' }}>
          {c.label}: {describeValue(c.before)} → {describeValue(c.after)}
        </Typography>
      ))}
    </Box>
  );
}

/** The restore waiting for a second approver, if any, and the last decision. */
export function PendingRestorePanel({ api, base, subject, canDecide, onApproved }: PendingRestorePanelProps): JSX.Element | null {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [notice, setNotice] = useState('');
  const requests = useQuery({ queryKey: restoreRequestsKey(base), queryFn: () => api.list() });
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: restoreRequestsKey(base) });
    await queryClient.invalidateQueries({ queryKey: settingsHistoryKey(subject) });
  };
  const approve = useMutation({
    mutationFn: ({ id, discardDraft }: { id: string; discardDraft: boolean }) => api.approve(id, discardDraft ? { discardDraft: true } : {}),
    onSuccess: async (decided) => {
      setNotice(decided.status === 'APPLIED'
        ? `Restore of ${decided.targetLabel} approved. It is in force now.`
        : `Restore of ${decided.targetLabel} approved; it is being put in force.`);
      await refresh();
      onApproved?.();
    },
    // A refusal that settled the request (expired, failed) changes the list.
    onError: async () => { await refresh(); },
  });
  // DEV-142 review: a monitoring draft in progress is never replaced silently.
  const draftInProgress = approve.error instanceof ApiError && approve.error.code === 'DRAFT_IN_PROGRESS';
  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.reject(id, reason),
    onSuccess: async (decided) => {
      setRejecting(false);
      setNotice(`Restore of ${decided.targetLabel} rejected. Nothing changed.`);
      await refresh();
    },
  });

  const pending: RestoreRequest | undefined = (requests.data ?? []).find((r) => r.status === 'PENDING');
  const applying = (requests.data ?? []).find((r) => r.status === 'APPLYING');
  // The newest request, when it ended without taking effect: say why once.
  const newest = (requests.data ?? [])[0];
  const ended = newest !== undefined && (newest.status === 'FAILED' || newest.status === 'EXPIRED') ? newest : undefined;
  if (requests.isError) return <Alert severity="error" sx={{ mb: 2 }}>Could not load the restore requests.</Alert>;
  if (applying !== undefined) {
    return <Alert severity="info" sx={{ mb: 2 }}>Restore of {applying.targetLabel} approved; it is being put in force. Reload in a moment.</Alert>;
  }
  if (pending === undefined) {
    if (notice !== '') return <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>;
    if (ended !== undefined) {
      return <Alert severity="warning" sx={{ mb: 2 }}>
        Restore of {ended.targetLabel} {ended.status === 'EXPIRED' ? 'expired' : 'failed'}: {ended.failureDetail ?? 'no reason recorded'}
      </Alert>;
    }
    return null;
  }
  const own = user?.id === pending.proposedBy;
  const proposer = pending.proposedByName ?? pending.proposedBy;
  const error = approve.isError && !draftInProgress ? message(approve.error, 'Could not approve the restore.') : reject.isError ? message(reject.error, 'Could not reject the restore.') : '';

  return (
    <Paper variant="outlined" role="region" aria-label="Restore waiting for approval" sx={{ p: 2, mb: 2, borderColor: 'warning.main' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip size="small" color="warning" label="Waiting for a second approver" />
        <Typography variant="subtitle2">Restore of {pending.targetLabel}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Proposed by {proposer} on {new Date(pending.proposedAt).toLocaleString(APP_LOCALE)}. Reason: {pending.reason}
      </Typography>
      <ChangeList changes={pending.changes} />
      {error !== '' ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
      {draftInProgress && canDecide && !own ? (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={<Button color="warning" disabled={approve.isPending} onClick={() => approve.mutate({ id: pending.id, discardDraft: true })}>
            Discard the draft and approve
          </Button>}
        >
          {message(approve.error, 'A draft is in progress.')}
        </Alert>
      ) : null}
      {canDecide ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button variant="contained" color="warning" disabled={own || approve.isPending} onClick={() => approve.mutate({ id: pending.id, discardDraft: false })}>
            {approve.isPending ? 'Approving…' : 'Approve restore'}
          </Button>
          <Button onClick={() => setRejecting(true)} disabled={reject.isPending}>{own ? 'Withdraw' : 'Reject'}</Button>
          {own ? (
            <Typography variant="caption" color="text.secondary">
              You proposed it, so another person who may change this section has to approve it (four eyes).
            </Typography>
          ) : null}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">Only people who may change this section can approve or reject it.</Typography>
      )}
      <RestoreReasonDialog
        open={rejecting}
        title={own ? 'Withdraw the restore' : 'Reject the restore'}
        description={`Restore of ${pending.targetLabel}, proposed by ${proposer}. Nothing changes; the reason is kept in the History.`}
        confirmLabel={own ? 'Withdraw with reason' : 'Reject with reason'}
        saving={reject.isPending}
        {...(reject.isError ? { error: message(reject.error, 'Could not reject the restore.') } : {})}
        onClose={() => setRejecting(false)}
        onConfirm={(reason) => reject.mutate({ id: pending.id, reason })}
      />
    </Paper>
  );
}

interface FourEyesRestoreProps {
  readonly api: FourEyesRestoreApi;
  readonly base: string;
  readonly subject: SettingsHistorySubject;
  /** The History entry's restore target being proposed, until proposed or cancelled. */
  readonly target: string | null;
  readonly onClearTarget: () => void;
  readonly canDecide: boolean;
  readonly onApproved?: () => void;
  /** Unsaved edits on the page: a restore waits until they are saved or discarded. */
  readonly editing?: boolean;
}

/**
 * The restore of a section that may run under four eyes (DEV-142): while a
 * History entry was chosen, the restore through the save bar (a dry run of
 * what it changes, a reason). With one approver (decision 18.10, today) it
 * takes effect at once; with four eyes switched on it is proposed, and the
 * pending request is shown with Approve and Reject. A monitoring draft in
 * progress is never replaced silently: the writer chooses to discard it.
 */
export function FourEyesRestore({ api, base, subject, target, onClearTarget, canDecide, onApproved, editing = false }: FourEyesRestoreProps): JSX.Element {
  const queryClient = useQueryClient();
  const preview = useQuery({
    queryKey: ['four-eyes-restore-preview', base, target],
    queryFn: () => api.preview(target ?? ''),
    enabled: target !== null && !editing,
    retry: false,
  });
  const [notice, setNotice] = useState('');
  const propose = useMutation({
    mutationFn: ({ reason, discardDraft }: { reason: string; discardDraft: boolean }) => api.propose(target ?? '', reason, discardDraft),
    onMutate: () => { setNotice(''); },
    onSuccess: async (result) => {
      onClearTarget();
      const label = `${result.targetLabel.charAt(0).toUpperCase()}${result.targetLabel.slice(1)}`;
      if (result.status === 'APPLIED') {
        setNotice(`${label} restored. It is in force now.`);
        onApproved?.();
      } else if (result.status === 'APPLYING') {
        setNotice(`Restore of ${result.targetLabel} is being put in force.`);
      }
      await queryClient.invalidateQueries({ queryKey: restoreRequestsKey(base) });
      await queryClient.invalidateQueries({ queryKey: settingsHistoryKey(subject) });
    },
  });
  const draftInProgress = propose.error instanceof ApiError && propose.error.code === 'DRAFT_IN_PROGRESS';
  let flow: JSX.Element | null = null;
  if (target !== null) {
    if (editing) {
      flow = <Alert severity="warning" action={<Button onClick={onClearTarget}>Cancel restore</Button>}>Save or discard your edits before restoring a version.</Alert>;
    } else if (preview.isLoading) {
      flow = <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
    } else if (preview.isError || preview.data === undefined) {
      flow = <Alert severity="error" action={<Button onClick={onClearTarget}>Close</Button>}>{message(preview.error, 'That version cannot be restored.')}</Alert>;
    } else {
      flow = (
        <RestoreSaveBar
          fourEyes={preview.data.needsSecondApprover}
          plan={{ label: preview.data.targetLabel, changes: preview.data.changes,
            warning: preview.data.needsSecondApprover
              ? 'Nothing changes until a second person who may change this section approves it.'
              : 'This takes effect at once.',
            // A draft in progress is replaced only when the writer ticks this.
            ...(draftInProgress ? { confirmLabel: 'Discard the draft in progress and restore' } : {}) }}
          saving={propose.isPending}
          {...(propose.isError ? { error: message(propose.error, 'The restore could not be saved.') } : {})}
          onConfirm={(reason) => propose.mutate({ reason, discardDraft: draftInProgress })}
          onCancel={onClearTarget}
        />
      );
    }
  }
  return (
    <>
      {notice !== '' ? <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert> : null}
      <PendingRestorePanel api={api} base={base} subject={subject} canDecide={canDecide} {...(onApproved === undefined ? {} : { onApproved })} />
      {flow}
    </>
  );
}

interface RestoreReasonDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel?: string;
  readonly saving: boolean;
  readonly error?: string;
  readonly onClose: () => void;
  readonly onConfirm: (reason: string) => void;
  readonly children?: ReactNode;
}

export function RestoreReasonDialog({ open, title, description, confirmLabel = 'Restore with reason', saving, error, onClose, onConfirm, children }: RestoreReasonDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  // A reason is never carried over to the next restore.
  useEffect(() => { if (!open) setReason(''); }, [open]);
  const close = (): void => { setReason(''); onClose(); };
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="restore-reason-title">
      <DialogTitle id="restore-reason-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>
        {children}
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          helperText={`At least ${String(RESTORE_MIN_REASON)} characters. Kept in the History.`}
          inputProps={{ name: 'nvx-settings-restore-reason', autoComplete: 'off', maxLength: 2000 }}
        />
        {error !== undefined ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Back</Button>
        <Button variant="contained" color="warning" disabled={saving || reason.trim().length < RESTORE_MIN_REASON}
          onClick={() => onConfirm(reason.trim())}>
          {saving ? 'Saving…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
